import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useModal } from '../context/Modal';
import SignupForm from './SignupForm';
const PLAN_SLUG = 'one-year-2026';

// Turn a daily-verse API reference like "John 3:16" or "1 Corinthians 13:1-3"
// into the regular Bible reader route (/bible/:version/:book/:chapter). Falls
// back to /bible-reading when the reference cannot be parsed so the button
// always leads somewhere sensible.
const buildDailyVerseReaderUrl = (reference) => {
  if (!reference) return '/bible-reading';
  const match = reference.trim().match(/^(.+?)\s+(\d+)(?::[\d\-–,\s]+)?$/);
  if (!match) return '/bible-reading';
  const book = match[1].trim();
  const chapter = parseInt(match[2], 10);
  if (!book || !Number.isFinite(chapter)) return '/bible-reading';
  return `/bible/ESV/${encodeURIComponent(book)}/${chapter}`;
};

// Outer cards use a soft dual-shadow with enough offset+blur to read as a
// clearly raised 3D panel above the page. Both colors come from
// --outer-shadow-* so they can be tuned per theme without touching inner
// pills or insets (which still use --shadow-light/dark).
const NEU_RAISED_LG = 'shadow-[-10px_-10px_22px_var(--outer-shadow-light),10px_10px_22px_var(--outer-shadow-dark)]';
const NEU_RAISED_MD = 'shadow-[-8px_-8px_18px_var(--outer-shadow-light),8px_8px_18px_var(--outer-shadow-dark)]';
// Shared shadow for on-page pill buttons and icon discs (hero CTAs,
// Sanctuary Tools icons). Same colors as the cards so everything sitting on
// the page shares one lighting model.
const PAGE_RAISED_MD = 'shadow-[-6px_-6px_14px_var(--outer-shadow-light),6px_6px_14px_var(--outer-shadow-dark)]';
const PAGE_RAISED_SM = 'shadow-[-4px_-4px_9px_var(--outer-shadow-light),4px_4px_9px_var(--outer-shadow-dark)]';
const NEU_RAISED_XS = 'shadow-[-3px_-3px_6px_var(--shadow-light),3px_3px_6px_var(--shadow-dark)]';
const NEU_INSET_SM = 'shadow-[inset_2px_2px_4px_var(--shadow-dark),inset_-2px_-2px_4px_var(--shadow-light)]';

const Icon = ({ name, className = '' }) => (
  <span
    className={`material-symbols-outlined leading-none ${className}`}
    aria-hidden="true"
  >
    {name}
  </span>
);

const HomePage = () => {
  const user = useSelector((state) => state.session.user);
  const { setModalContent } = useModal();
  const openSignupModal = () => setModalContent(<SignupForm />);

  // One-Year (통독) plan — server-backed (§9 of v2). The homepage card reads
  // { plan, today, progress } from the backend so completion syncs across
  // devices and the timezone is resolved server-side.
  const clientTz = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (_e) {
      return null;
    }
  }, []);

  const [planData, setPlanData] = useState({ plan: null, today: null, progress: null, loading: true, error: null });

  useEffect(() => {
    let cancelled = false;
    const tzHeader = clientTz ? { 'X-Client-Timezone': clientTz } : {};
    const load = async () => {
      try {
        const [planRes, todayRes, progressRes] = await Promise.all([
          fetch(`/api/reading-plans/${PLAN_SLUG}`, { headers: tzHeader, credentials: 'include' }),
          fetch(`/api/reading-plans/${PLAN_SLUG}/today`, { headers: tzHeader, credentials: 'include' }),
          fetch(`/api/reading-plans/${PLAN_SLUG}/progress`, { headers: tzHeader, credentials: 'include' }),
        ]);
        const [plan, today, progress] = await Promise.all([
          planRes.ok ? planRes.json() : null,
          todayRes.ok ? todayRes.json() : null,
          progressRes.ok ? progressRes.json() : null,
        ]);
        if (!cancelled) setPlanData({ plan, today, progress, loading: false, error: null });
      } catch (err) {
        if (!cancelled) setPlanData((prev) => ({ ...prev, loading: false, error: err?.message || 'error' }));
      }
    };
    load();
    return () => { cancelled = true; };
  }, [clientTz]);

  const planToday = planData.today;
  const planPosition = {
    day: planToday?.sequence || null,
    total: planData.plan?.day_count || 365,
  };
  const planCompletedCount = planData.progress?.completed_count || 0;
  const planPercent = planData.progress?.percent ?? 0;

  const [dailyVerse, setDailyVerse] = useState('');
  const [dailyReference, setDailyReference] = useState('');
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIosBanner, setShowIosBanner] = useState(false);
  // Sky (powder) is the default and only theme. The variable is kept so the
  // data-theme attribute below still resolves the CSS custom properties.
  const theme = 'powder';

  useEffect(() => {
    let cancelled = false;
    const fetchDailyVerse = async () => {
      try {
        const response = await fetch(
          'https://beta.ourmanna.com/api/v1/get?format=json&order=daily',
          { headers: { accept: 'application/json' } }
        );
        const data = await response.json();
        if (cancelled) return;
        setDailyVerse(data?.verse?.details?.text || '');
        setDailyReference(data?.verse?.details?.reference || '');
      } catch (error) {
        console.error('Error fetching the daily verse:', error);
      }
    };
    fetchDailyVerse();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const isIos = () => /iphone|ipad|ipod/.test(window.navigator.userAgent.toLowerCase());
    const isStandalone = () =>
      window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;

    const dismissedUntil = localStorage.getItem('iosBannerDismissed');
    if (dismissedUntil && Date.now() < parseInt(dismissedUntil, 10)) return;
    if (isIos() && !isStandalone()) setShowIosBanner(true);
  }, []);

  useEffect(() => {
    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
      setShowInstallBanner(true);
    };
    const handleAppInstalled = () => setShowInstallBanner(false);
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallApp = () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    deferredPrompt.userChoice.finally(() => {
      setDeferredPrompt(null);
      setShowInstallBanner(false);
    });
  };

  const handleCloseIosBanner = (persist) => {
    setShowIosBanner(false);
    if (persist) {
      const expiration = Date.now() + 7 * 24 * 60 * 60 * 1000;
      localStorage.setItem('iosBannerDismissed', expiration.toString());
    }
  };


  const weekdays = useMemo(() => {
    const today = new Date();
    const day = today.getDay(); // 0 = Sun
    return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((label, index) => {
      let state = 'pending';
      if (index < day) state = 'done';
      else if (index === day) state = 'today';
      return { label, state };
    });
  }, []);

  const streakDays = weekdays.filter((d) => d.state === 'done').length;

  const features = [
    {
      icon: 'flare',
      title: 'Daily Verse',
      body:
        'A curated morning passage presented simultaneously in Korean and English to awaken spiritual contemplation.',
      cta: 'Explore Lectionary',
      to: '/devotions/today',
    },
    {
      icon: 'compare_arrows',
      title: 'Compare Versions',
      body:
        'Read KRV, SAE, ESV, and NIV side by side to discern nuanced Greek and Hebrew idioms without losing reading flow.',
      cta: 'Parallel Reader',
      to: '/version-comparison',
    },
    {
      icon: 'stylus_note',
      title: 'Highlights & Memos',
      body:
        'Mark key phrases with verse-anchored theological notes and personal reflections that remain tied to the text forever.',
      cta: 'View My Sanctuary',
      to: '/my-memos',
    },
  ];

  return (
    <div
      data-theme={theme}
      className="min-h-screen bg-page-bg text-on-surface font-inter transition-colors"
    >
      <main className="w-full">
        <div className="w-full max-w-[1440px] mx-auto px-margin-mobile md:px-margin-neu py-space-xl flex flex-col gap-space-xl">
          {/* HERO */}
          <section className="grid grid-cols-1 lg:grid-cols-12 gap-gutter items-center">
            <div className="lg:col-span-6 flex flex-col gap-space-md">
              <h1 className="font-noto-serif text-headline-xl text-neu-primary tracking-tight">
                Read Scripture, mark what speaks to you, and keep your notes with the verse.
              </h1>
              <p className="text-body-lg text-slate-muted max-w-xl">
                Bible Memos lets you read in Korean and English side by side, highlight verses with
                tactile precision, and record contemplative notes you can always return to.
              </p>
              <div className="flex flex-row items-stretch sm:items-center gap-3 sm:gap-space-md sm:flex-wrap pt-space-xs">
                <Link
                  to="/devotions/today"
                  className={`flex-1 sm:flex-none min-w-0 inline-flex items-center justify-center gap-1.5 sm:gap-space-xs px-3 sm:px-space-xl min-h-14 rounded-full bg-accent-cta text-white font-semibold text-sm sm:text-label-lg leading-tight text-center ${PAGE_RAISED_MD} hover:bg-accent-cta-hover hover:shadow-[-3px_-3px_8px_var(--outer-shadow-light),3px_3px_8px_var(--outer-shadow-dark)] active:shadow-[inset_3px_3px_6px_rgba(0,0,0,0.3),inset_-3px_-3px_6px_rgba(255,255,255,0.18)] transition-all`}
                >
                  <Icon name="menu_book" className="text-white text-[20px] sm:text-[22px] shrink-0" />
                  <span className="min-w-0">
                    <span className="sm:hidden">Today&apos;s passage</span>
                    <span className="hidden sm:inline">Read today&apos;s passage</span>
                  </span>
                </Link>
                <Link
                  to="/bible-reading"
                  className={`flex-1 sm:flex-none min-w-0 inline-flex items-center justify-center gap-1.5 sm:gap-space-xs px-3 sm:px-space-xl min-h-14 rounded-full bg-canvas-base text-neu-primary font-semibold text-sm sm:text-label-lg leading-tight text-center ${PAGE_RAISED_MD} hover:text-accent-cta hover:shadow-[-3px_-3px_8px_var(--outer-shadow-light),3px_3px_8px_var(--outer-shadow-dark)] active:shadow-[inset_3px_3px_6px_var(--outer-shadow-dark),inset_-3px_-3px_6px_var(--outer-shadow-light)] transition-all`}
                >
                  <Icon name="chrome_reader_mode" className="text-accent-cta text-[20px] sm:text-[22px] shrink-0" />
                  <span className="min-w-0">
                    <span className="sm:hidden">Open Bible</span>
                    <span className="hidden sm:inline">Open the Bible</span>
                  </span>
                </Link>
              </div>
            </div>

            {/* Today's verse card — glassmorphism. Sits on the pale sky-blue
                page, so a translucent white gradient + backdrop blur reads as
                a frosted glass sheet. */}
            <div className="lg:col-span-6">
              <div
                className="rounded-[1.5rem] sm:rounded-[2rem] p-space-md sm:p-space-lg flex flex-col gap-space-sm relative"
                style={{
                  // Outer glass — pure blur, no color tint. Saturate is kept
                  // near-neutral so the sky page isn't super-saturated into a
                  // blue-tinted card; the only "color" you see is what the
                  // backdrop-filter is showing through.
                  background:
                    'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 55%, rgba(255,255,255,0.05) 100%)',
                  backdropFilter: 'blur(18px) saturate(110%)',
                  WebkitBackdropFilter: 'blur(18px) saturate(110%)',
                  border: '1px solid rgba(255,255,255,0.35)',
                  boxShadow:
                    '0 10px 30px rgba(31,41,55,0.12), inset 0 1px 0 rgba(255,255,255,0.4)',
                }}
              >
                <div className="flex items-center gap-space-xs">
                  <div
                    className="w-9 h-9 rounded-full flex items-center justify-center"
                    style={{
                      background:
                        'linear-gradient(135deg, rgba(255,255,255,0.75), rgba(255,255,255,0.35))',
                      border: '1px solid rgba(255,255,255,0.65)',
                      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.7)',
                    }}
                  >
                    <Icon name="calendar_today" className="text-accent-gold-deep text-[18px]" />
                  </div>
                  <p className="text-label-sm uppercase tracking-wider text-neu-primary/85 font-semibold">
                    Daily Verse{dailyReference ? ` · ${dailyReference}` : ''}
                  </p>
                </div>

                <div
                  className="rounded-[1rem] p-space-sm sm:p-space-md flex flex-col"
                  style={{
                    // Verse box — sky-family tint + inset shadows so it
                    // reads as pressed into the glass card, not floating on
                    // top. Dark cast on the top-left, subtle light on the
                    // bottom-right, and a thin dark hairline at the top
                    // edge for the "carved trench" cue.
                    background:
                      'linear-gradient(180deg, rgba(210,225,238,0.55), rgba(210,225,238,0.35))',
                    border: '1px solid rgba(255,255,255,0.25)',
                    boxShadow:
                      'inset 3px 3px 8px rgba(31,41,55,0.16), inset -3px -3px 8px rgba(255,255,255,0.35), inset 0 1px 0 rgba(31,41,55,0.10)',
                  }}
                >
                  <p className="font-noto-serif text-scripture-body text-scripture-ink leading-relaxed">
                    {dailyVerse ? (
                      `“${dailyVerse}”`
                    ) : (
                      <>
                        {'“He will wipe every tear from their eyes. '}
                        <mark className="bg-verse-highlight text-scripture-ink px-1 rounded-sm shadow-[0_1px_2px_rgba(0,0,0,0.06)] border-b border-accent-terracotta">
                          There will be no more death
                        </mark>
                        {' or mourning or crying or pain, for the old order of things has passed away.”'}
                      </>
                    )}
                  </p>
                </div>

                <div className="flex items-center justify-end">
                  <Link
                    to={buildDailyVerseReaderUrl(dailyReference)}
                    className="inline-flex items-center gap-1 px-space-sm py-1.5 rounded-full text-label-md text-neu-primary/85 hover:text-neu-primary transition-all"
                  >
                    <Icon name="play_circle" className="text-[16px] text-neu-secondary" />
                    <span>Start Reading</span>
                  </Link>
                </div>
              </div>
            </div>
          </section>

          {/* READING HUB — two plans, logged-in users only.
              Left card  = 일년일독 통독표 (One-Year Whole-Bible Reading Plan)
                           TODO: wire to plan data once Excel is uploaded.
              Right card = 매일성경 · this week (daily plan, weekly view)
                           TODO: wire to daily_passages API for the current week. */}
          {user && (
          <section className="flex flex-col gap-space-md">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-space-xs">
              <div>
                <span className="text-label-sm uppercase tracking-widest text-accent-gold-deep font-bold">
                  Reading Plans
                </span>
                <h2 className="font-noto-serif text-headline-lg text-neu-primary">Your Reading Plans</h2>
              </div>
              <p className="text-body-neu-md text-slate-muted">
                Track both your one-year whole-Bible plan and your daily 매일성경 rhythm.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-gutter">
              <div className={`lg:col-span-6 rounded-[2rem] bg-canvas-base p-space-lg ${NEU_RAISED_MD} flex flex-col justify-between gap-space-md`}>
                <div className="flex items-start justify-between gap-space-sm flex-wrap">
                  <div className="flex items-center gap-space-sm">
                    <div className={`w-12 h-12 rounded-full bg-canvas-base flex items-center justify-center ${NEU_RAISED_XS}`}>
                      <Icon name="menu_book" className="text-accent-gold-deep text-[24px]" />
                    </div>
                    <div>
                      <p className="text-label-sm text-accent-gold-deep uppercase tracking-wider font-semibold">
                        One-Year Plan · 통독
                      </p>
                      <h3 className="font-noto-serif text-headline-md text-neu-primary">
                        {planToday?.reference_en || planToday?.reference_ko || 'Whole-Bible Reading'}
                      </h3>
                      <p className="text-body-neu-md text-slate-muted">
                        {planData.loading
                          ? 'Loading today’s reading…'
                          : planToday
                            ? `${planToday.date} · ${planToday.reference_ko || planToday.reference_en}`
                            : planData.error
                              ? 'Plan data unavailable.'
                              : 'No published plan yet — ask an administrator to publish one.'}
                      </p>
                    </div>
                  </div>
                  <span className={`px-space-sm py-1 rounded-full text-label-md bg-canvas-base ${NEU_INSET_SM} text-neu-primary font-semibold`}>
                    {planData.loading
                      ? '— % Done'
                      : user
                        ? `${planCompletedCount} / ${planPosition.total} · ${planPercent}%`
                        : 'Sign in for progress'}
                  </span>
                </div>

                <div className="flex flex-col gap-1.5">
                  <div className="flex justify-between text-label-sm text-slate-muted">
                    <span>Year Progress</span>
                    <span>
                      {planPosition.day
                        ? `Day ${planPosition.day} of ${planPosition.total}`
                        : `Day — of ${planPosition.total}`}
                    </span>
                  </div>
                  <div className={`w-full h-3 rounded-full bg-canvas-base p-0.5 ${NEU_INSET_SM}`}>
                    <div
                      className="h-full rounded-full bg-neu-secondary transition-all"
                      style={{ width: `${planPercent}%` }}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between pt-space-xs gap-space-sm flex-wrap">
                  <div className="flex flex-col gap-1">
                    <span className="text-body-neu-md text-slate-muted italic">
                      {planToday
                        ? `Today’s reading · ${planToday.weekday || ''}`
                        : 'Today’s reading will appear once the plan loads.'}
                    </span>
                    <Link
                      to={`/reading-plans/${PLAN_SLUG}`}
                      className="text-label-sm text-accent-cta font-semibold hover:text-accent-cta-hover transition-colors inline-flex items-center gap-1"
                    >
                      View full plan <Icon name="arrow_forward" className="text-[13px]" />
                    </Link>
                  </div>
                  <Link
                    to={
                      planToday
                        ? `/bible-reading/plan/${PLAN_SLUG}/${planToday.date}`
                        : '/bible-reading'
                    }
                    className="inline-flex items-center gap-space-xs px-space-md py-2.5 rounded-full bg-accent-cta text-white text-label-md font-semibold shadow-[-3px_-3px_7px_var(--outer-shadow-light),4px_5px_10px_rgba(0,0,0,0.22)] hover:bg-accent-cta-hover active:shadow-[inset_2px_2px_5px_rgba(0,0,0,0.3),inset_-2px_-2px_5px_rgba(255,255,255,0.2)] transition-all"
                  >
                    <Icon name="menu_book" className="text-white text-[16px]" />
                    <span>Read today’s passage</span>
                  </Link>
                </div>
              </div>

              <div className={`lg:col-span-6 rounded-[2rem] bg-canvas-base p-space-lg ${NEU_RAISED_MD} flex flex-col justify-between gap-space-md`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-label-sm text-accent-gold-deep uppercase tracking-wider font-semibold">
                      매일성경 · Daily
                    </p>
                    <h3 className="font-noto-serif text-headline-sm text-neu-primary">
                      This Week
                    </h3>
                  </div>
                  <div className={`w-9 h-9 rounded-full bg-canvas-base flex items-center justify-center ${NEU_RAISED_XS}`}>
                    <Icon name="local_fire_department" className="text-neu-secondary text-[20px]" />
                  </div>
                </div>

                <div className="grid grid-cols-7 gap-2 text-center">
                  {weekdays.map(({ label, state }) => (
                    <div key={label} className="flex flex-col items-center gap-1.5">
                      <span
                        className={
                          state === 'today'
                            ? 'text-label-sm text-accent-gold-deep font-bold'
                            : 'text-label-sm text-slate-muted'
                        }
                      >
                        {label}
                      </span>
                      <div
                        className={`w-10 h-10 rounded-full bg-canvas-base flex items-center justify-center ${
                          state === 'today'
                            ? NEU_RAISED_XS + ' text-neu-primary'
                            : state === 'done'
                              ? NEU_INSET_SM + ' text-neu-secondary'
                              : NEU_RAISED_XS + ' text-slate-muted opacity-60'
                        }`}
                      >
                        {state === 'done' && <Icon name="check" className="text-[18px]" />}
                        {state === 'today' && (
                          <Icon name="radio_button_checked" className="text-[18px] text-accent-gold-deep" />
                        )}
                        {state === 'pending' && <Icon name="schedule" className="text-[16px]" />}
                      </div>
                    </div>
                  ))}
                </div>

                <div className={`flex items-center gap-space-xs p-2.5 rounded-[1rem] bg-canvas-base ${NEU_INSET_SM}`}>
                  <Icon name="arrow_back_ios_new" className="text-neu-secondary text-[18px]" />
                  <p className="text-label-md text-on-surface-variant">
                    Open today’s 매일성경 to keep your{' '}
                    <strong>
                      {streakDays}-day reading streak
                    </strong>{' '}
                    unbroken.
                  </p>
                </div>
              </div>
            </div>
          </section>
          )}

          {/* RECENT MEMOS — logged-in users only */}
          {user && (
          <section className={`rounded-[2rem] bg-canvas-base p-space-lg md:p-space-xl ${NEU_RAISED_MD} flex flex-col gap-space-md`}>
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-space-xs">
              <div>
                <span className="text-label-sm uppercase tracking-widest text-accent-gold-deep font-bold">
                  Notes
                </span>
                <h2 className="font-noto-serif text-headline-md text-neu-primary">Recent Memos</h2>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-gutter">
              {[
                {
                  ref: 'Mark 2:5 · ESV',
                  quote: '“Son, your sins are forgiven.”',
                  body: "Jesus answers the need the friends didn’t name first.",
                  accent: 'var(--accent-cta)',
                },
                {
                  ref: '느헤미야 6:3 · KRV',
                  quote: '내가 이제 큰 역사를 하니 내려가지 못하겠노라',
                  body: '사명의 크기로 결정하기 — 설교 1대지',
                  accent: '#4d9a7a',
                },
                {
                  ref: 'Psalm 119:105 · NIV',
                  quote: '“Your word is a lamp for my feet…”',
                  body: 'A lamp shows the next step, not the whole road.',
                  accent: '#4d7ba8',
                },
              ].map((memo) => (
                <article
                  key={memo.ref}
                  className="rounded-[1rem] bg-canvas-base p-space-md flex flex-col gap-space-xs shadow-[inset_2px_2px_5px_var(--shadow-dark),inset_-2px_-2px_5px_var(--shadow-light)]"
                  style={{ borderLeft: `3px solid ${memo.accent}` }}
                >
                  <p className="text-label-md text-neu-primary font-semibold">{memo.ref}</p>
                  <p className="font-noto-serif text-body-lg text-scripture-ink italic leading-7">
                    {memo.quote}
                  </p>
                  <p className="text-body-neu-md text-slate-muted">{memo.body}</p>
                </article>
              ))}
            </div>

            <Link
              to="/my-memos/memos"
              className="text-label-md text-accent-cta font-semibold hover:text-accent-cta-hover transition-colors inline-flex items-center gap-1 self-start"
            >
              See all memos <Icon name="arrow_forward" className="text-[14px]" />
            </Link>
          </section>
          )}

          {/* FEATURES — marketing surface, hidden for signed-in users */}
          {!user && (
          <section className="flex flex-col gap-space-md">
            <div className="text-center max-w-2xl mx-auto flex flex-col gap-space-xs">
              <span className="text-label-sm uppercase tracking-widest text-accent-gold-deep font-bold">
                Methodical Devotion
              </span>
              <h2 className="font-noto-serif text-headline-lg text-neu-primary">
                Built for Thoughtful Study
              </h2>
              <p className="text-body-neu-md text-slate-muted">
                Tactile digital surfaces crafted to minimize cognitive noise, deepen linguistic
                comprehension, and anchor memory.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-gutter">
              {features.map((f) => (
                <Link
                  key={f.title}
                  to={f.to}
                  className={`rounded-[2rem] bg-canvas-base p-space-lg ${NEU_RAISED_MD} flex flex-col items-center text-center gap-space-md group hover:shadow-[-4px_-4px_10px_var(--outer-shadow-light),4px_4px_10px_var(--outer-shadow-dark)] transition-all`}
                >
                  <div className="w-16 h-16 rounded-full bg-accent-terracotta-soft flex items-center justify-center shadow-[inset_-2px_-2px_5px_rgba(0,0,0,0.06),inset_2px_2px_5px_rgba(255,255,255,0.7),-3px_-3px_7px_rgba(255,255,255,0.9),4px_5px_12px_rgba(0,0,0,0.18)] group-hover:shadow-[inset_-2px_-2px_5px_rgba(0,0,0,0.06),inset_2px_2px_6px_rgba(255,255,255,0.85),-5px_-5px_11px_rgba(255,255,255,1),8px_10px_20px_rgba(0,0,0,0.24)] group-hover:-translate-y-1 transition-all duration-200 ease-out">
                    <Icon name={f.icon} className="text-accent-gold-deep text-[28px]" />
                  </div>
                  <div className="flex flex-col gap-space-xs">
                    <h3 className="font-noto-serif text-headline-sm text-neu-primary">{f.title}</h3>
                    <p className="text-body-neu-md text-slate-muted">{f.body}</p>
                  </div>
                  <span className="text-label-md text-accent-gold-deep font-semibold mt-auto flex items-center gap-1">
                    {f.cta} <Icon name="arrow_forward" className="text-[14px]" />
                  </span>
                </Link>
              ))}
            </div>
          </section>
          )}

          {/* CTA — Create Account. Hidden for signed-in users (they already
              have an account). Sky-blue background is fixed across themes;
              the inset shadow gives it a debossed "carved into page" look. */}
          {!user && (
          <section className="rounded-[2rem] p-space-lg md:p-space-xl bg-[#d4dce1] shadow-[inset_5px_5px_12px_var(--shadow-dark),inset_-5px_-5px_12px_var(--shadow-light)] flex flex-col md:flex-row items-center justify-between gap-space-lg">
            <div className="flex flex-col gap-space-xs max-w-2xl text-left">
              <div className="inline-flex items-center gap-1.5 text-accent-gold-deep text-label-sm uppercase tracking-wider font-bold">
                <Icon name="cloud_sync" className="text-[16px]" />
                <span>Seamless Multi-Device Continuity</span>
              </div>
              <h3 className="font-noto-serif text-headline-md text-neu-primary">
                Keep your notes and reflections in one sacred space.
              </h3>
              <p className="text-body-neu-md text-slate-muted">
                Create an account to sync verse highlights, Korean vocabulary tags, and sermon
                reflections seamlessly across your phone, tablet, and desktop sanctuary.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-center gap-space-md w-full md:w-auto">
              <button
                type="button"
                onClick={openSignupModal}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-space-xs px-space-xl h-12 rounded-full bg-accent-cta text-label-lg text-white font-semibold shadow-[0_6px_16px_rgba(0,0,0,0.25)] hover:bg-accent-cta-hover active:shadow-[inset_3px_3px_6px_rgba(0,0,0,0.3),inset_-3px_-3px_6px_rgba(255,255,255,0.2)] transition-all"
              >
                <Icon name="person_add" className="text-white text-[20px]" />
                <span>Create Account</span>
              </button>
            </div>
          </section>
          )}
        </div>
      </main>

      {/* FOOTER */}
      <footer className={`w-full bg-page-bg mt-space-xl pt-space-xl pb-space-lg shadow-[inset_0_4px_8px_var(--footer-inset-tint)]`}>
        <div className="max-w-[1440px] mx-auto px-margin-mobile md:px-margin-neu">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-gutter mb-space-xl">
            <div className="flex flex-col gap-space-sm">
              <div className="flex items-center gap-space-xs">
                <div className={`w-8 h-8 rounded-full bg-canvas-base flex items-center justify-center ${NEU_INSET_SM}`}>
                  <Icon name="menu_book" className="text-accent-gold-deep text-[18px]" />
                </div>
                <span className="font-noto-serif text-headline-sm text-neu-primary">Bible Memos</span>
              </div>
              <p className="text-body-neu-md text-slate-muted pr-space-md">
                A quiet digital sanctuary carved for contemplative Scripture reflection,
                dual-translation analysis, and sermon notation.
              </p>
            </div>

            <div className="flex flex-col gap-space-xs">
              <h4 className="text-label-lg text-neu-primary uppercase tracking-wider mb-space-xs">
                Scripture
              </h4>
              <Link to="/bible-reading" className="text-body-neu-md text-on-surface-variant hover:text-accent-gold-deep transition-colors">Old Testament</Link>
              <Link to="/bible-reading" className="text-body-neu-md text-on-surface-variant hover:text-accent-gold-deep transition-colors">New Testament</Link>
              <Link to="/devotions/today" className="text-body-neu-md text-on-surface-variant hover:text-accent-gold-deep transition-colors">Daily Lectionary</Link>
              <Link to="/version-comparison" className="text-body-neu-md text-on-surface-variant hover:text-accent-gold-deep transition-colors">Parallel Translations</Link>
            </div>

            <div className="flex flex-col gap-space-xs">
              <h4 className="text-label-lg text-neu-primary uppercase tracking-wider mb-space-xs">
                Reflections
              </h4>
              <Link to="/my-memos/highlights" className="text-body-neu-md text-on-surface-variant hover:text-accent-gold-deep transition-colors">Verse Highlights</Link>
              <Link to="/my-memos/memos" className="text-body-neu-md text-on-surface-variant hover:text-accent-gold-deep transition-colors">Prayer Journal</Link>
              <Link to="/my-memos/sermons" className="text-body-neu-md text-on-surface-variant hover:text-accent-gold-deep transition-colors">Sermon Transcripts</Link>
              <Link to="/my-memos" className="text-body-neu-md text-on-surface-variant hover:text-accent-gold-deep transition-colors">Audio Recitations</Link>
            </div>

            <div className="flex flex-col gap-space-xs">
              <h4 className="text-label-lg text-neu-primary uppercase tracking-wider mb-space-xs">
                Sanctuary Tools
              </h4>
              <Link to="/about/explore-biblememos" className="text-body-neu-md text-on-surface-variant hover:text-accent-gold-deep transition-colors">Philosophy of Quietness</Link>
              <Link to="/version-comparison" className="text-body-neu-md text-on-surface-variant hover:text-accent-gold-deep transition-colors">Bilingual Concordance</Link>
              <Link to="/about/explore-biblememos" className="text-body-neu-md text-on-surface-variant hover:text-accent-gold-deep transition-colors">Offline Sync Guide</Link>
              <div className="flex items-center gap-space-md mt-space-md">
                <Link
                  to="/my-memos/highlights"
                  aria-label="Verse highlights"
                  className={`w-12 h-12 rounded-full bg-canvas-base flex items-center justify-center ${PAGE_RAISED_SM} text-accent-cta hover:text-accent-cta-hover hover:shadow-[-2px_-2px_5px_var(--outer-shadow-light),2px_2px_5px_var(--outer-shadow-dark)] active:shadow-[inset_3px_3px_6px_var(--outer-shadow-dark),inset_-3px_-3px_6px_var(--outer-shadow-light)] transition-all`}
                >
                  <Icon name="bookmark" className="text-[22px]" />
                </Link>
                <Link
                  to="/devotions/today"
                  aria-label="Daily devotion audio"
                  className={`w-12 h-12 rounded-full bg-canvas-base flex items-center justify-center ${PAGE_RAISED_SM} text-accent-cta hover:text-accent-cta-hover hover:shadow-[-2px_-2px_5px_var(--outer-shadow-light),2px_2px_5px_var(--outer-shadow-dark)] active:shadow-[inset_3px_3px_6px_var(--outer-shadow-dark),inset_-3px_-3px_6px_var(--outer-shadow-light)] transition-all`}
                >
                  <Icon name="graphic_eq" className="text-[22px]" />
                </Link>
                <Link
                  to="/my-memos/sermons"
                  aria-label="Sermon transcripts"
                  className={`w-12 h-12 rounded-full bg-canvas-base flex items-center justify-center ${PAGE_RAISED_SM} text-accent-cta hover:text-accent-cta-hover hover:shadow-[-2px_-2px_5px_var(--outer-shadow-light),2px_2px_5px_var(--outer-shadow-dark)] active:shadow-[inset_3px_3px_6px_var(--outer-shadow-dark),inset_-3px_-3px_6px_var(--outer-shadow-light)] transition-all`}
                >
                  <Icon name="auto_stories" className="text-[22px]" />
                </Link>
              </div>
            </div>
          </div>

          <div className="pt-space-md flex flex-col md:flex-row items-center justify-between gap-space-sm shadow-[inset_0_1px_0_var(--shadow-light)]">
            <p className="text-body-neu-md text-slate-muted">
              © {new Date().getFullYear()} Bible Memos · A quiet sanctuary for bilingual Scripture study
            </p>
            <div className="flex items-center gap-space-md">
              <Link to="/about/who-we-are" className="text-label-md text-slate-muted hover:text-on-surface transition-colors">Privacy</Link>
              <Link to="/about/who-we-are" className="text-label-md text-slate-muted hover:text-on-surface transition-colors">Terms</Link>
              <Link to="/settings" className="text-label-md text-slate-muted hover:text-on-surface transition-colors">Quiet Mode Settings</Link>
            </div>
          </div>
        </div>
      </footer>

      {showInstallBanner && (
        <div className={`fixed bottom-6 left-6 right-6 md:left-auto md:w-96 bg-canvas-base rounded-[2rem] p-space-md ${NEU_RAISED_LG} flex justify-between items-center gap-space-sm z-40`}>
          <span className="text-label-lg text-neu-primary font-semibold">
            Install this app on your device
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className={`bg-canvas-base text-neu-primary font-semibold py-2 px-4 rounded-full ${NEU_RAISED_XS}`}
              onClick={handleInstallApp}
            >
              Install
            </button>
            <button
              type="button"
              className="text-slate-muted p-2"
              onClick={() => setShowInstallBanner(false)}
              aria-label="Dismiss"
            >
              <Icon name="close" className="text-[18px]" />
            </button>
          </div>
        </div>
      )}

      {showIosBanner && (
        <div className={`fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:w-96 bg-canvas-base rounded-[2rem] p-space-md ${NEU_RAISED_LG} flex justify-between items-start gap-space-sm z-40`}>
          <div className="flex flex-col gap-1">
            <p className="text-label-lg text-neu-primary font-semibold">Add to Home Screen</p>
            <p className="text-body-neu-md text-slate-muted">
              Tap the <strong>Share</strong> icon in Safari and select{' '}
              <strong>“Add to Home Screen”</strong>.
            </p>
          </div>
          <button
            type="button"
            className="text-slate-muted p-2"
            onClick={() => handleCloseIosBanner(true)}
            aria-label="Dismiss"
          >
            <Icon name="close" className="text-[18px]" />
          </button>
        </div>
      )}
    </div>
  );
};

export default HomePage;
